import React from 'react';

import { Box, BoxProps, Flex, Spoiler, Switch, Text } from '@mantine/core';
import { IconAlertTriangle, IconUser } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { InfoComponent } from 'components/InfoComponent';
import { InputLabelWithInfo } from 'components/InputLabelWithInfo';
import { UpgradePlanButton } from 'components/UpgradePlanButton';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { useTranslation } from 'react-i18next';
import { ConversationTakeoverService } from 'services/conversation-takeover-service';
import { NotificationService } from 'services/notification-service';
import { TAKEOVER_WINDOW_DURATION_TRANSLATION_KEY } from 'shared/constants/conversations';
import { Conversation, ConversationSourceType, TakeoverStatus } from 'shared/interfaces/Conversation';

interface ConversationTakeoveContentProps extends BoxProps {
  conversation: Conversation;
  onConversationUpdate: (updatedConversation: Conversation) => void;
  isPastMetaTakeoverWindow: boolean;
}

export function ConversationTakeoverContent({
  conversation,
  onConversationUpdate,
  isPastMetaTakeoverWindow,
  ...boxProps
}: ConversationTakeoveContentProps) {
  const { t } = useTranslation();
  const [subscription] = useSubscriptionContext();

  const updateTakeoverDataMutation = useMutation({
    mutationFn: ConversationTakeoverService.updateTakeoverData,
    onSuccess: (updatedConversation: Conversation) => onConversationUpdate(updatedConversation),
    onError: () => NotificationService.showResult(false, t('FEED_BACK.CONVERSATION.FAILURE.UPDATE_TAKEOVER_STATUS'))
  });

  const extractUserInfo = (conversation: Conversation) => {
    switch (conversation.source.type) {
      case ConversationSourceType.WHATSAPP:
        return `${conversation.source.whatsAppInfo?.senderName} - ${conversation.source.whatsAppInfo?.senderPhoneNumber}`;
      case ConversationSourceType.FACEBOOK:
        return conversation.source.facebookInfo?.senderName;
      case ConversationSourceType.INSTAGRAM:
        return conversation.source.instagramInfo?.senderUsername;
      default:
        return '';
    }
  };

  return (
    <Flex
      direction="row"
      align="flex-start"
      justify="space-between"
      {...boxProps}
      sx={{ position: 'relative', ...boxProps.sx }}>
      <Box>
        <Flex gap={5} align="center">
          <IconUser />
          <Text fw={500}>{extractUserInfo(conversation)}</Text>
        </Flex>
        {conversation.takeoverData?.reason && (
          <Flex direction="column" gap={3} align="flex-start" w="100%" mt="xs">
            <Flex align="center" gap={5}>
              <Text fw={500}>{t('SMART_ASSISTANT.CONVERSATIONS_SECTION.TAKEOVER.REASON.LABEL')}</Text>
              <InfoComponent contentTranslationKey="SMART_ASSISTANT.CONVERSATIONS_SECTION.TAKEOVER.REASON.INFO" />
            </Flex>

            <Spoiler maxHeight={35} maw={400} showLabel={t('APP.ACTIONS.SHOW_MORE')} hideLabel={t('APP.ACTIONS.HIDE')}>
              <Text>{conversation.takeoverData.reason}</Text>
            </Spoiler>
          </Flex>
        )}
      </Box>

      {!subscription.product.enableTakeover ? (
        <UpgradePlanButton
          showLogo
          size="xs"
          variant="default"
          dark
          label={t('SMART_ASSISTANT.CONVERSATIONS_SECTION.TAKEOVER.UPGRADE_PLAN_LABEL')}
        />
      ) : (
        <Flex direction="column" align="flex-end" gap={7}>
          {isPastMetaTakeoverWindow ? (
            <Flex direction="row" align="center" gap={5}>
              <Text size="sm">{t('SMART_ASSISTANT.CONVERSATIONS_SECTION.TAKEOVER.WINDOW_RESTRICTION.LABEL')}</Text>
              <InfoComponent
                contentTranslationKey={t('SMART_ASSISTANT.CONVERSATIONS_SECTION.TAKEOVER.WINDOW_RESTRICTION.INFO', {
                  windowDuration: t(
                    `SMART_ASSISTANT.CONVERSATIONS_SECTION.TAKEOVER.WINDOW_RESTRICTION.WINDOW_DURATION.${TAKEOVER_WINDOW_DURATION_TRANSLATION_KEY}`
                  )
                })}
                position="bottom"
                withinPortal={true}
              />
            </Flex>
          ) : (
            <Switch
              size="xs"
              disabled={!subscription.product.enableTakeover}
              label={
                <InputLabelWithInfo
                  mb={0}
                  label={t('SMART_ASSISTANT.CONVERSATIONS_SECTION.TAKEOVER.LABEL')}
                  infoContentTranslationKey={t('SMART_ASSISTANT.CONVERSATIONS_SECTION.TAKEOVER.DESCRIPTION')}
                  infoPosition="bottom"
                />
              }
              color="orange"
              checked={conversation.takeoverData?.status === TakeoverStatus.ON}
              onChange={() =>
                updateTakeoverDataMutation.mutate({
                  conversationId: conversation._id,
                  takeoverStatus:
                    conversation.takeoverData?.status === TakeoverStatus.ON ? TakeoverStatus.OFF : TakeoverStatus.ON
                })
              }
            />
          )}

          {conversation.takeoverData?.status === TakeoverStatus.ON && !isPastMetaTakeoverWindow ? (
            <Flex justify="flex-end" align="center" gap={5}>
              <IconAlertTriangle color="gray" style={{ width: 16, height: 16, marginBottom: 1 }} />
              <Text color="dimmed" fz="xs">
                {t('SMART_ASSISTANT.CONVERSATIONS_SECTION.TAKEOVER.ALERT')}
              </Text>
            </Flex>
          ) : null}
        </Flex>
      )}
    </Flex>
  );
}
